import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { type ReactNode } from 'react';
import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { Divider } from '@/app/components/divider';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { type ModalRegisterFormProps } from '@/app/components/sections/register/modals/types';
import { INTEREST_TYPE_TIL } from '@/app/constants/calculator';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { buildCalculatorRegisterRemark, getCalculatorMonthlyInstallment } from '@/app/lcv/components/sections/calculator/calculator-helpers';
import { PrivilegeTable } from '@/app/lcv/components/sections/calculator/result/privilege-table';
import styles from '@/app/lcv/components/sections/calculator/styles.module.css';
import { CALCULATOR_DATA_TEST } from '@/app/lcv/components/sections/calculator/tracking';
import { type Month, type PrivilegeTableProps } from '@/app/lcv/types/calculator';
import type { LCVTilInterest, Model } from '@/app/types/models/lcv';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { formatPrice } from '@/app/utils/format';
import type { LcvModelWithFieldKey } from '@/services/lineup-model/lcv-product-compare/types';
import type { LcvModelCategory, LcvModelColor } from '@/types/tis-cms/types';
export interface CalculatorResultProps {
  modelCategory: LcvModelCategory;
  model: LcvModelWithFieldKey;
  color: LcvModelColor;
  price: string;
  payment: string;
  interest: string;
  listMonth: Month[];
  month: Month;
  onReCalculate: () => void;
  data: PrivilegeTableProps;
  isTilLeasing?: boolean;
  tilInterestList?: LCVTilInterest[];
  vehicleType: ModalRegisterFormProps['vehicleType'];
  provinces: ModalRegisterFormProps['provinces'];
  /** From `getSpecialCampaign`. */
  isSpecialCampaignOn?: boolean;
}
interface ItemValueProps {
  label: ReactNode;
  value: ReactNode;
  active?: boolean;
  position?: 'first' | 'last' | 'auto';
  testId?: string;
}
const ModalRegisterForm = dynamic(() => import('@/app/components/sections/register/modals/modal-register-form').then(mod => mod.ModalRegisterForm), {
  ssr: false
});

/** Used instead of the modal while `showInCalculator` is on. */
const CAMPAIGN_REGISTER_PATH = '/register';
const MonthlyItem = t0 => {
  const $ = _c(26);
  const {
    label,
    value,
    active: t1,
    position: t2
  } = t0;
  const active = t1 === undefined ? false : t1;
  const position = t2 === undefined ? "auto" : t2;
  const isFirst = position === "first";
  const isLast = position === "last";
  let t3;
  if ($[0] !== active) {
    t3 = cn("relative flex w-full lg:block", styles.monthlyItem, {
      "before:absolute before:inset-x-0 before:-inset-y-0.25 before:-top-0.25 before:z-20 before:border-4 before:border-red-500 last:before:rounded-b-md lg:last:before:rounded-bl-none": active
    });
    $[0] = active;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = !isLast;
  let t5;
  if ($[2] !== active || $[3] !== isFirst || $[4] !== isLast || $[5] !== t4) {
    t5 = cn("max-w-33 basis-33 bg-gray-500 p-4 text-center lg:w-full lg:max-w-none", "border-silver-300 border border-r-0 not-last:border-b-0", {
      "bg-red-500": active,
      "rounded-tl-md": isFirst,
      "rounded-bl-md lg:rounded-bl-none": isLast,
      "border-b-0": t4
    });
    $[2] = active;
    $[3] = isFirst;
    $[4] = isLast;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== label) {
    t6 = <span className="font-noto-bold text-white">{label}</span>;
    $[7] = label;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = !isLast;
  let t9;
  if ($[12] !== active || $[13] !== isFirst || $[14] !== isLast || $[15] !== t8) {
    t9 = cn("grow bg-white p-4 text-center lg:w-full lg:max-w-none", "border-silver-300 border border-l-0 lg:border-r-0 lg:border-b lg:border-l", {
      "relative after:absolute after:inset-0 after:bg-red-500 after:opacity-5": active,
      "rounded-tr-md lg:rounded-tr-none lg:rounded-bl-md": isFirst,
      "rounded-br-md lg:border-r": isLast,
      "border-b-0": t8
    });
    $[12] = active;
    $[13] = isFirst;
    $[14] = isLast;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== value) {
    t10 = <span>{value}</span>;
    $[17] = value;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t3 || $[24] !== t7) {
    t12 = <div className={t3}>{t7}{t11}</div>;
    $[22] = t11;
    $[23] = t3;
    $[24] = t7;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
const ResultDetailsItem = t0 => {
  const $ = _c(11);
  const {
    label,
    value,
    testId: t1
  } = t0;
  const testId = t1 === undefined ? "result-details-item" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn("grid", styles.resultRowTemplate);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== label) {
    t3 = <span>{label}:</span>;
    $[1] = label;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== testId) {
    t4 = createDataAttributes({
      test: testId
    });
    $[3] = testId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4 || $[6] !== value) {
    t5 = <span {...t4}> {value}</span>;
    $[5] = t4;
    $[6] = value;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <div className={t2}>{t3}{t5}</div>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
export const CalculatorResult = t0 => {
  const $ = _c(60);
  const {
    ...props
  } = t0;
  const [isOpen, t1] = useDisclosure(false);
  const {
    close: onCloseModal,
    open: onOpenModal
  } = t1;
  const registerHref = props.isSpecialCampaignOn ? `${CAMPAIGN_REGISTER_PATH}?model=${props.vehicleType}` : undefined;
  const getPaymentByMonth = month => getCalculatorMonthlyInstallment({
    price: props.price,
    payment: props.payment,
    month,
    isTilLeasing: props.isTilLeasing,
    tilInterestList: props.tilInterestList,
    interest: props.interest
  });
  const remark = buildCalculatorRegisterRemark({
    modelCategoryName: props.modelCategory.nameTh,
    modelName: props.model.name,
    colorName: props?.color.color?.nameTh ?? "",
    price: props.price,
    payment: props.payment,
    monthValue: props.month.value,
    isTilLeasing: props.isTilLeasing,
    interest: props.interest
  });
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = createDataAttributes({
      test: CALCULATOR_DATA_TEST.calculator
    });
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = createDataAttributes({
      test: `${CALCULATOR_DATA_TEST.calculator}-result`
    });
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = createDataAttributes({
      test: "vehicle-category"
    });
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = createDataAttributes({
      test: "vehicle-grade"
    });
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createDataAttributes({
      test: "vehicle-price"
    });
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = parseFloat(props.price);
  let t8;
  if ($[5] !== t7) {
    t8 = formatPrice(t7);
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t8) {
    t9 = <span className="font-noto-bold text-gray-900" {...t6}>{t8}</span>;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Divider color="gray" className="my-3 h-px" />;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const t11 = parseInt(props.payment);
  let t12;
  if ($[10] !== t11) {
    t12 = t11.toLocaleString();
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const t13 = `${t12} บาท`;
  let t14;
  if ($[12] !== t13) {
    t14 = <ResultDetailsItem label={"\u0E08\u0E33\u0E19\u0E27\u0E19\u0E40\u0E07\u0E34\u0E19\u0E14\u0E32\u0E27\u0E19\u0E4C"} value={t13} testId="down-payment-amount" />;
    $[12] = t13;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const t15 = props.isTilLeasing ? INTEREST_TYPE_TIL : `${props.interest} %`;
  let t16;
  if ($[14] !== t15) {
    t16 = <ResultDetailsItem label={"\u0E2D\u0E31\u0E15\u0E23\u0E32\u0E14\u0E2D\u0E01\u0E40\u0E1A\u0E35\u0E49\u0E22"} value={t15} testId="interest-rate" />;
    $[14] = t15;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  const t17 = `${props.month.value} เดือน`;
  let t18;
  if ($[16] !== t17) {
    t18 = <ResultDetailsItem label={"\u0E23\u0E30\u0E22\u0E30\u0E40\u0E27\u0E25\u0E32\u0E1C\u0E48\u0E2D\u0E19\u0E0A\u0E33\u0E23\u0E30"} value={t17} testId="interest-term" />;
    $[16] = t17;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  let t19;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = createDataAttributes({
      test: "monthly-payment"
    });
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  const t20 = "py-6 lg:py-14";
  let t21;
  let t22;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = createDataAttributes({
      test: `${CALCULATOR_DATA_TEST.calculator}-monthly-payment`
    });
    t22 = <h3 className="font-noto-bold text-sub-h2 mb-2 text-gray-900">ตารางการผ่อนชำระ (บาท)</h3>;
    $[19] = t21;
    $[20] = t22;
  } else {
    t21 = $[19];
    t22 = $[20];
  }
  const t23 = "grid overflow-hidden rounded-md lg:grid-flow-col";
  const t24 = parseInt(props.payment);
  let t25;
  if ($[21] !== t24) {
    t25 = t24.toLocaleString();
    $[21] = t24;
    $[22] = t25;
  } else {
    t25 = $[22];
  }
  let t26;
  if ($[23] !== t25) {
    t26 = <MonthlyItem label={"\u0E40\u0E07\u0E34\u0E19\u0E14\u0E32\u0E27\u0E19\u0E4C"} value={t25} position="first" />;
    $[23] = t25;
    $[24] = t26;
  } else {
    t26 = $[24];
  }
  const t27 = parseInt(props.price) - parseInt(props.payment);
  let t28;
  if ($[25] !== t27) {
    t28 = t27.toLocaleString();
    $[25] = t27;
    $[26] = t28;
  } else {
    t28 = $[26];
  }
  let t29;
  if ($[27] !== t28) {
    t29 = <MonthlyItem label={"\u0E22\u0E2D\u0E14\u0E08\u0E31\u0E14"} value={t28} />;
    $[27] = t28;
    $[28] = t29;
  } else {
    t29 = $[28];
  }
  const t30 = props.listMonth.map((o, i) => <MonthlyItem key={i} label={`${o.label} เดือน`} value={getPaymentByMonth(o.value).toLocaleString()} active={o.value === props.month.value} position={i === props.listMonth.length - 1 ? "last" : "auto"} />);
  let t31;
  if ($[29] !== t26 || $[30] !== t29 || $[31] !== t30) {
    t31 = <div className={t23}>{t26}{t29}{t30}</div>;
    $[29] = t26;
    $[30] = t29;
    $[31] = t30;
    $[32] = t31;
  } else {
    t31 = $[32];
  }
  const t32 = props.onReCalculate;
  let t33;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = createDataAttributes({
      test: "button-recalculate"
    });
    $[33] = t33;
  } else {
    t33 = $[33];
  }
  let t34;
  if ($[34] !== props.onReCalculate) {
    t34 = <Button variant="secondary" onClick={t32} {...t33}>คำนวณใหม่</Button>;
    $[34] = props.onReCalculate;
    $[35] = t34;
  } else {
    t34 = $[35];
  }
  let t35;
  if ($[36] !== onOpenModal || $[37] !== registerHref) {
    t35 = window.FLUTTER_LEAD ? <Button onClick={_temp}>ลงทะเบียน</Button> : <Button name="register" href={registerHref} onClick={registerHref ? undefined : onOpenModal} {...createDataAttributes({
      test: "button-register"
    })}>ลงทะเบียน</Button>;
    $[36] = onOpenModal;
    $[37] = registerHref;
    $[38] = t35;
  } else {
    t35 = $[38];
  }
  let t36;
  if ($[39] !== t34 || $[40] !== t35) {
    t36 = <ButtonGroup className="mt-6 justify-center">{t34}{t35}</ButtonGroup>;
    $[39] = t34;
    $[40] = t35;
    $[41] = t36;
  } else {
    t36 = $[41];
  }
  let t37;
  if ($[42] !== t21 || $[43] !== t22 || $[44] !== t31 || $[45] !== t36) {
    t37 = <div className={t20} {...t21}>{t22}{t31}{t36}</div>;
    $[42] = t21;
    $[43] = t22;
    $[44] = t31;
    $[45] = t36;
    $[46] = t37;
  } else {
    t37 = $[46];
  }
  let t38;
  if ($[47] !== props.data) {
    t38 = <PrivilegeTable {...props.data} />;
    $[47] = props.data;
    $[48] = t38;
  } else {
    t38 = $[48];
  }
  let t39;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = createDataAttributes({
      test: `${CALCULATOR_DATA_TEST.calculator}-result-remark`
    });
    $[49] = t39;
  } else {
    t39 = $[49];
  }
  let t40;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <span>ตรีเพชรอีซูซุลิสซิ่ง</span>;
    $[50] = t40;
  } else {
    t40 = $[50];
  }
  let t41;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="text-caption where-span:text-inline-wrap where-a:link mt-8" {...t39}>หมายเหตุ: โปรแกรมคำนวณนี้ ใช้คำนวณเพื่อเป็นข้อมูลประกอบในการตัดสินใจ เท่านั้น ไม่สามารถนำมาเป็นหลักฐานอ้างอิงได้ กรุณาศึกษาเพิ่มเติมจากผู้จำหน่ายอีซูซุทั่วประเทศ หรือสอบถามข้อมูลได้ที่ บริษัท {t40} จำกัด โทรศัพท์ <a href="tel:1366">1366</a>* เงื่อนไขเป็นไปตามที่บริษัทฯ กำหนด</div>;
    $[51] = t41;
  } else {
    t41 = $[51];
  }
  let t42;
  if ($[52] !== isOpen || $[53] !== onCloseModal || $[54] !== props.model?.category?.title || $[55] !== props.modelCategory || $[56] !== props.provinces || $[57] !== props.vehicleType || $[58] !== remark) {
    t42 = !!isOpen && <ModalRegisterForm theme="light" formId="calculator" formName={"\u0E04\u0E33\u0E19\u0E27\u0E13\u0E04\u0E48\u0E32\u0E07\u0E27\u0E14"} formTracking={{
      formType: "calculator_popup"
    }} campaignVersion="1.0.0" model={(props.model?.category?.title ?? props.modelCategory.title) as Model} vehicleType={props.vehicleType} provinces={props.provinces} remark={remark} onClose={onCloseModal} />;
    $[52] = isOpen;
    $[53] = onCloseModal;
    $[54] = props.model?.category?.title;
    $[55] = props.modelCategory;
    $[56] = props.provinces;
    $[57] = props.vehicleType;
    $[58] = remark;
    $[59] = t42;
  } else {
    t42 = $[59];
  }
  return <Section size="md" className="bg-silver-50 text-gray-700" {...t2}><Container><div className="lg:flex lg:justify-center" {...t3}><div className="mx-auto w-full max-w-119 grow justify-self-end lg:mx-0"><Image priority={true} src={props.color.front?.url ?? ""} alt={props.modelCategory?.nameTh} width={600} height={338} sizes="(min-width: 1024px) 800px, 90vw" objectFit="contain" aspectRatio="aspect-16-9" /></div><div><div className="where-span:block space-y-1"><span {...t4}>{props.modelCategory?.nameTh}</span><span {...t5}>{props.model?.name}</span>{!!props.color && <span {...createDataAttributes({
              test: "vehicle-color"
            })}>สีรถ {props?.color.color?.nameTh ?? ""}</span>}{t9}</div>{t10}<div className="grid gap-y-1">{t14}{t16}{t18}<span className="font-noto-bold text-sub-h2 text-gray-900" {...t19}>{formatPrice(getPaymentByMonth(props.month.value))} / เดือน</span></div></div></div>{t37}{t38}{t41}</Container>{t42}</Section>;
};
function _temp() {
  window.FLUTTER_LEAD?.postMessage("");
}